import { RecaptchaError } from '@hub/lib/recaptcha';
import type { RecaptchaAction, RecaptchaHeaders } from '@platform/api';
import { captureException, withScope } from '@platform/observability';
import { useCallback } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

const DEFAULT_TIMEOUT_MS = 5_000;

/**
 * Returns a function that executes reCAPTCHA v3 for the given action
 * and returns headers to pass into the request.
 *
 * Place `<RecaptchaProvider>` in the page that needs protection, not globally, and never in the
 * component that calls this hook: a context is invisible to the component rendering it, and the
 * library's default `executeRecaptcha` throws instead of being absent, so the guard below misses it.
 *
 * ```tsx
 * // RegistrationPage.tsx
 * <RecaptchaProvider>
 *   <RegistrationForm />
 * </RecaptchaProvider>
 *
 * // RegistrationForm.tsx
 * const executeRecaptcha = useRecaptcha();
 * const recaptcha = await executeRecaptcha('register');
 * postApiOnboardingRegister(data, { recaptcha });
 * ```
 */
export function useRecaptcha() {
  const { executeRecaptcha } = useGoogleReCaptcha();

  // Stable identity so callers can memoize on it (see `usePaymentApiAdapters`); the provider's
  // own `executeRecaptcha` only changes when the grecaptcha instance does.
  // Generic in the action so the returned token carries it in its type: the
  // generated `*WithRecaptcha` wrappers only accept a token minted for their own
  // action, which is what stops a token being sent to the wrong endpoint.
  return useCallback(
    async <A extends RecaptchaAction>(action: A): Promise<RecaptchaHeaders<A> | undefined> => {
      if (!executeRecaptcha) {
        const hasGlobal = typeof window !== 'undefined' && 'grecaptcha' in window;
        const reason = hasGlobal
          ? 'reCAPTCHA not ready — provider may be missing'
          : 'reCAPTCHA script failed to load (blocked or network error)';
        withScope((scope) => {
          scope.setTag('recaptcha_action', action);
          captureException(new Error(reason));
        });
        return undefined;
      }

      let timeoutId: ReturnType<typeof setTimeout>;
      try {
        const token = await Promise.race([
          executeRecaptcha(action).then((t) => {
            clearTimeout(timeoutId);
            return t;
          }),
          new Promise<never>((_resolve, reject) => {
            timeoutId = setTimeout(() => reject(new Error(`reCAPTCHA timed out after ${DEFAULT_TIMEOUT_MS}ms`)), DEFAULT_TIMEOUT_MS);
          }),
        ]);
        return { token, action };
      } catch (error) {
        throw new RecaptchaError(action, { cause: error });
      }
    },
    [executeRecaptcha],
  );
}
